import { Card, Col, Row, Statistic, Progress, Tooltip } from "antd"
import {
  SoundOutlined,
  CheckCircleOutlined,
  TrophyOutlined,
  DashboardOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons"
import type React from "react"
import type { SpeechAssessmentStats } from "@/services/log/speechAssessmentApi"

interface SpeechAssessmentSummaryCardsProps {
  stats?: SpeechAssessmentStats
  loading?: boolean
}

export const SpeechAssessmentSummaryCards: React.FC<SpeechAssessmentSummaryCardsProps> = ({
  stats,
  loading = false,
}) => {
  const total = stats?.total ?? 0
  const successCount = stats?.success_count ?? 0
  const successRate = stats?.success_rate ?? 0
  const avgPronScore = stats?.avg_pron_score ?? 0
  const avgLatency = stats?.avg_latency ?? 0

  // Pron score color: < 50 red, 50-80 orange/gold, > 80 green
  const getScoreColor = (score: number) => {
    if (score >= 80) return "#52c41a"
    if (score >= 50) return "#faad14"
    return "#f5222d"
  }

  // Latency color: < 1500 green, 1500-3000 orange, > 3000 red
  const getLatencyColor = (latency: number) => {
    if (latency <= 1500) return "#52c41a"
    if (latency <= 3000) return "#faad14"
    return "#f5222d"
  }

  return (
    <Row gutter={[16, 16]} className="mb-2">
      {/* 1. Tổng lượt phát âm */}
      <Col xs={24} sm={12} lg={6}>
        <Card className="h-full shadow-xs transition-shadow hover:shadow-sm" loading={loading}>
          <div className="flex items-start justify-between">
            <div>
              <div className="mb-1 font-medium text-gray-500 text-xs uppercase tracking-wider">Tổng lượt phát âm</div>
              <Statistic value={total} valueStyle={{ color: "#1677ff", fontWeight: 700, fontSize: "1.75rem" }} />
              <div className="mt-1 text-gray-400 text-xs">
                Thành công: <strong className="text-gray-600">{successCount}</strong> lượt
              </div>
            </div>
            <div className="rounded-xl bg-blue-50 p-3 text-2xl text-blue-500">
              <SoundOutlined />
            </div>
          </div>
        </Card>
      </Col>

      {/* 2. Tỷ lệ thành công */}
      <Col xs={24} sm={12} lg={6}>
        <Card className="h-full shadow-xs transition-shadow hover:shadow-sm" loading={loading}>
          <div className="flex items-start justify-between">
            <div className="flex-1 pr-2">
              <div className="mb-1 font-medium text-gray-500 text-xs uppercase tracking-wider">Tỷ lệ thành công</div>
              <Statistic
                value={successRate}
                suffix="%"
                valueStyle={{
                  color: successRate >= 95 ? "#52c41a" : successRate >= 80 ? "#faad14" : "#f5222d",
                  fontWeight: 700,
                  fontSize: "1.75rem",
                }}
              />
              <div className="mt-2">
                <Progress
                  percent={successRate}
                  showInfo={false}
                  strokeColor={successRate >= 95 ? "#52c41a" : successRate >= 80 ? "#faad14" : "#f5222d"}
                  size="small"
                />
              </div>
            </div>
            <div className="rounded-xl bg-green-50 p-3 text-2xl text-green-500">
              <CheckCircleOutlined />
            </div>
          </div>
        </Card>
      </Col>

      {/* 3. Điểm phát âm trung bình */}
      <Col xs={24} sm={12} lg={6}>
        <Card className="h-full shadow-xs transition-shadow hover:shadow-sm" loading={loading}>
          <div className="flex items-start justify-between">
            <div>
              <div className="mb-1 flex items-center gap-1 font-medium text-gray-500 text-xs uppercase tracking-wider">
                <span>Điểm phát âm TB</span>
                <Tooltip title="Điểm trung bình của các lượt đánh giá thành công">
                  <InfoCircleOutlined className="cursor-pointer text-gray-400 text-xs" />
                </Tooltip>
              </div>
              <Statistic
                value={avgPronScore}
                suffix="/ 100"
                valueStyle={{
                  color: getScoreColor(avgPronScore),
                  fontWeight: 700,
                  fontSize: "1.75rem",
                }}
              />
              <div className="mt-1 text-gray-400 text-xs">
                {avgPronScore >= 80 ? "Đạt chuẩn tốt" : avgPronScore >= 50 ? "Khá" : "Cần cải thiện"}
              </div>
            </div>
            <div className="rounded-xl bg-purple-50 p-3 text-2xl text-purple-500">
              <TrophyOutlined />
            </div>
          </div>
        </Card>
      </Col>

      {/* 4. Độ trễ trung bình */}
      <Col xs={24} sm={12} lg={6}>
        <Card className="h-full shadow-xs transition-shadow hover:shadow-sm" loading={loading}>
          <div className="flex items-start justify-between">
            <div>
              <div className="mb-1 flex items-center gap-1 font-medium text-gray-500 text-xs uppercase tracking-wider">
                <span>Độ trễ trung bình</span>
                <Tooltip title="Thời gian Azure phản hồi kết quả (ms)">
                  <InfoCircleOutlined className="cursor-pointer text-gray-400 text-xs" />
                </Tooltip>
              </div>
              <Statistic
                value={avgLatency}
                suffix="ms"
                valueStyle={{
                  color: getLatencyColor(avgLatency),
                  fontWeight: 700,
                  fontSize: "1.75rem",
                }}
              />
              <div className="mt-1 text-gray-400 text-xs">
                {avgLatency > 3000 ? "Phản hồi chậm (> 3s)" : "Tốc độ ổn định"}
              </div>
            </div>
            <div className="rounded-xl bg-orange-50 p-3 text-2xl text-orange-500">
              <DashboardOutlined />
            </div>
          </div>
        </Card>
      </Col>
    </Row>
  )
}

export default SpeechAssessmentSummaryCards
